"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { SocialAuthButtons } from '@/components/auth/SocialAuthButtons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, User } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import posthog from 'posthog-js';
import { trackSignUp } from '@/lib/analytics/gtm';
import { trackFbLead, trackFbCompleteRegistration, trackFbLogin } from '@/lib/analytics/facebook-pixel';
import { getSafeRedirect } from '@/lib/safe-redirect';
function AuthContent() {
  const $ = _c(133);
  const router = useRouter();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = createClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const supabase = t0;
  const [isLoading, setIsLoading] = useState(false);
  let t1;
  if ($[1] !== searchParams) {
    t1 = searchParams.get("email");
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const emailParam = t1;
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabParam === "signup" ? "signup" : "login");
  const [loginEmail, setLoginEmail] = useState(emailParam || "");
  const [loginOtp, setLoginOtp] = useState("");
  const [loginOtpSent, setLoginOtpSent] = useState(false);
  const [loginError, setLoginError] = useState(searchParams.get("error") === "oauth" ? "Sign-in failed. Please try again." : "");
  const [loginMessage, setLoginMessage] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState(emailParam || "");
  const [signupOtp, setSignupOtp] = useState("");
  const [signupOtpSent, setSignupOtpSent] = useState(false);
  const [signupError, setSignupError] = useState("");
  const [signupMessage, setSignupMessage] = useState("");
  let t2;
  if ($[3] !== searchParams) {
    t2 = searchParams.get("next");
    $[3] = searchParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const nextParam = t2;
  let t3;
  if ($[5] !== nextParam) {
    t3 = getSafeRedirect(nextParam);
    $[5] = nextParam;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const redirectTo = t3;
  let t4;
  if ($[7] !== router) {
    t4 = target => {
      if (target.startsWith("http")) {
        window.location.href = target;
        return;
      }
      router.replace(target);
    };
    $[7] = router;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const navigateTo = t4;
  let t5;
  let t6;
  if ($[9] !== navigateTo || $[10] !== redirectTo) {
    t5 = () => {
      let isMounted = true;
      const checkSession = async () => {
        const {
          data,
          error
        } = await supabase.auth.getUser();
        if (!isMounted) {
          return;
        }
        if (!error && data.user) {
          navigateTo(redirectTo);
        }
      };
      checkSession();
      return () => {
        isMounted = false;
      };
    };
    t6 = [navigateTo, redirectTo, supabase];
    $[9] = navigateTo;
    $[10] = redirectTo;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== redirectTo) {
    t7 = () => {
      if (typeof window === "undefined") {
        return "";
      }
      const baseUrl = `${window.location.origin}/auth/oauth/callback`;
      return `${baseUrl}?next=${encodeURIComponent(redirectTo)}`;
    };
    $[13] = redirectTo;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const getRedirectUrl = t7;
  let t8;
  if ($[15] !== getRedirectUrl || $[16] !== loginEmail) {
    t8 = async e => {
      e.preventDefault();
      setIsLoading(true);
      setLoginError("");
      setLoginMessage("");
      const {
        error: error_0
      } = await supabase.auth.signInWithOtp({
        email: loginEmail,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: getRedirectUrl()
        }
      });
      if (error_0) {
        if (error_0.message === "Signups not allowed for otp") {
          setLoginError("This email is not registered. Go to Sign Up to create an account.");
        } else {
          setLoginError(error_0.message);
        }
      } else {
        setLoginOtpSent(true);
        setLoginMessage("We sent a verification code to your email.");
      }
      setIsLoading(false);
    };
    $[15] = getRedirectUrl;
    $[16] = loginEmail;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleSendLoginOtp = t8;
  let t9;
  if ($[18] !== loginEmail || $[19] !== loginOtp || $[20] !== navigateTo || $[21] !== redirectTo) {
    t9 = async e_0 => {
      e_0.preventDefault();
      setIsLoading(true);
      setLoginError("");
      setLoginMessage("");
      const {
        error: error_1
      } = await supabase.auth.verifyOtp({
        email: loginEmail,
        token: loginOtp,
        type: "email"
      });
      if (error_1) {
        setLoginError(error_1.message);
      } else {
        const {
          data: t10
        } = await supabase.auth.getUser();
        const {
          user
        } = t10;
        trackFbLogin({
          userId: user?.id
        });
        posthog.capture("user_logged_in", {
          method: "email"
        });
        navigateTo(redirectTo);
      }
      setIsLoading(false);
    };
    $[18] = loginEmail;
    $[19] = loginOtp;
    $[20] = navigateTo;
    $[21] = redirectTo;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleVerifyLoginOtp = t9;
  let t10;
  if ($[23] !== getRedirectUrl || $[24] !== signupEmail) {
    t10 = async e_1 => {
      e_1.preventDefault();
      setIsLoading(true);
      setSignupError("");
      setSignupMessage("");
      const {
        error: error_2
      } = await supabase.auth.signInWithOtp({
        email: signupEmail,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: getRedirectUrl()
        }
      });
      if (error_2) {
        setSignupError(error_2.message);
      } else {
        setSignupOtpSent(true);
        setSignupMessage("We sent a verification code to your email.");
        trackFbLead();
      }
      setIsLoading(false);
    };
    $[23] = getRedirectUrl;
    $[24] = signupEmail;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleSendSignupOtp = t10;
  let t11;
  if ($[26] !== navigateTo || $[27] !== redirectTo || $[28] !== signupEmail || $[29] !== signupName || $[30] !== signupOtp) {
    t11 = async e_2 => {
      e_2.preventDefault();
      setIsLoading(true);
      setSignupError("");
      setSignupMessage("");
      const {
        error: error_3
      } = await supabase.auth.verifyOtp({
        email: signupEmail,
        token: signupOtp,
        type: "email"
      });
      if (error_3) {
        setSignupError(error_3.message);
      } else {
        await supabase.auth.updateUser({
          data: {
            full_name: signupName
          }
        });
        trackSignUp(signupName, signupEmail);
        trackFbCompleteRegistration({
          status: "success"
        });
        posthog.capture("user_signed_up", {
          method: "email"
        });
        navigateTo(redirectTo);
      }
      setIsLoading(false);
    };
    $[26] = navigateTo;
    $[27] = redirectTo;
    $[28] = signupEmail;
    $[29] = signupName;
    $[30] = signupOtp;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const handleVerifySignupOtp = t11;
  let t12;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <TabsList className="grid w-full grid-cols-2 mb-8 h-12"><TabsTrigger value="login" className="text-base">Sign In</TabsTrigger><TabsTrigger value="signup" className="text-base">Sign Up</TabsTrigger></TabsList>;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="text-center mb-6"><h2 className="text-2xl font-bold text-foreground">Welcome back</h2><p className="text-muted-foreground mt-1">Sign in to continue to your account</p></div>;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== redirectTo) {
    t14 = <SocialAuthButtons nextUrl={redirectTo} />;
    $[34] = redirectTo;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="relative">{t15}<div className="relative flex justify-center text-xs"><span className="bg-background px-3 text-muted-foreground">or continue with email</span></div></div>;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const t17 = loginOtpSent ? handleVerifyLoginOtp : handleSendLoginOtp;
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Label htmlFor="login-email">Email address</Label>;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = e_3 => setLoginEmail(e_3.target.value);
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const t21 = isLoading || loginOtpSent;
  let t22;
  if ($[41] !== loginEmail || $[42] !== t21) {
    t22 = <div className="space-y-2">{t18}<div className="relative">{t19}<Input id="login-email" type="email" placeholder="you@example.com" className="pl-10 h-11" required={true} value={loginEmail} onChange={t20} disabled={t21} /></div></div>;
    $[41] = loginEmail;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== isLoading || $[45] !== loginOtp || $[46] !== loginOtpSent) {
    t23 = loginOtpSent && <div className="space-y-2"><Label htmlFor="login-otp">Verification Code</Label><Input id="login-otp" type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="Enter the 6-digit code" className="h-11" required={true} value={loginOtp} onChange={e_4 => setLoginOtp(e_4.target.value)} disabled={isLoading} /></div>;
    $[44] = isLoading;
    $[45] = loginOtp;
    $[46] = loginOtpSent;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== loginMessage) {
    t24 = loginMessage && <p className="text-sm text-muted-foreground">{loginMessage}</p>;
    $[48] = loginMessage;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== loginError) {
    t25 = loginError && <p className="text-sm text-destructive">{loginError}</p>;
    $[50] = loginError;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  const t26 = loginOtpSent ? isLoading ? "Verifying..." : "Verify & Sign In" : isLoading ? "Sending code..." : "Continue";
  let t27;
  if ($[52] !== isLoading || $[53] !== loginOtpSent) {
    t27 = !loginOtpSent && !isLoading && <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>;
    $[52] = isLoading;
    $[53] = loginOtpSent;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== isLoading || $[56] !== t26 || $[57] !== t27) {
    t28 = <Button type="submit" className="w-full h-11 rounded-md" disabled={isLoading}>{t26}{t27}</Button>;
    $[55] = isLoading;
    $[56] = t26;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== isLoading || $[60] !== loginOtpSent) {
    t29 = loginOtpSent && <Button type="button" variant="ghost" className="w-full h-11" onClick={() => {
      setLoginOtpSent(false);
      setLoginOtp("");
      setLoginMessage("");
    }} disabled={isLoading}>Use a different email</Button>;
    $[59] = isLoading;
    $[60] = loginOtpSent;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== t28 || $[63] !== t29) {
    t30 = <div className="flex flex-col gap-2">{t28}{t29}</div>;
    $[62] = t28;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Link href="/terms" className="text-primary hover:underline">Terms of Use</Link>;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <p className="text-xs text-muted-foreground text-center mt-4">By continuing, you agree to our{" "}{t31}{" "}and{" "}<Link href="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.</p>;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] !== t17 || $[68] !== t22 || $[69] !== t23 || $[70] !== t24 || $[71] !== t25 || $[72] !== t30) {
    t33 = <form onSubmit={t17} className="space-y-4">{t22}{t23}{t24}{t25}{t30}{t32}</form>;
    $[67] = t17;
    $[68] = t22;
    $[69] = t23;
    $[70] = t24;
    $[71] = t25;
    $[72] = t30;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t14 || $[75] !== t33) {
    t34 = <TabsContent value="login" className="space-y-6">{t13}{t14}{t16}{t33}</TabsContent>;
    $[74] = t14;
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="text-center mb-6"><h2 className="text-2xl font-bold text-foreground">Create an account</h2><p className="text-muted-foreground mt-1">Sign up to get started</p></div>;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] !== redirectTo) {
    t36 = <SocialAuthButtons nextUrl={redirectTo} />;
    $[78] = redirectTo;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <div className="relative">{t37}<div className="relative flex justify-center text-xs"><span className="bg-background px-3 text-muted-foreground">or continue with email</span></div></div>;
    $[81] = t38;
  } else {
    t38 = $[81];
  }
  const t39 = signupOtpSent ? handleVerifySignupOtp : handleSendSignupOtp;
  let t40;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <Label htmlFor="signup-name">Full Name</Label>;
    $[82] = t40;
  } else {
    t40 = $[82];
  }
  let t41;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />;
    $[83] = t41;
  } else {
    t41 = $[83];
  }
  let t42;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = e_5 => setSignupName(e_5.target.value);
    $[84] = t42;
  } else {
    t42 = $[84];
  }
  const t43 = isLoading || signupOtpSent;
  let t44;
  if ($[85] !== signupName || $[86] !== t43) {
    t44 = <div className="space-y-2">{t40}<div className="relative">{t41}<Input id="signup-name" type="text" placeholder="John Doe" className="pl-10 h-11" required={true} value={signupName} onChange={t42} disabled={t43} /></div></div>;
    $[85] = signupName;
    $[86] = t43;
    $[87] = t44;
  } else {
    t44 = $[87];
  }
  let t45;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <Label htmlFor="signup-email">Email</Label>;
    $[88] = t45;
  } else {
    t45 = $[88];
  }
  let t46;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />;
    $[89] = t46;
  } else {
    t46 = $[89];
  }
  let t47;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = e_6 => setSignupEmail(e_6.target.value);
    $[90] = t47;
  } else {
    t47 = $[90];
  }
  const t48 = isLoading || signupOtpSent;
  let t49;
  if ($[91] !== signupEmail || $[92] !== t48) {
    t49 = <div className="space-y-2">{t45}<div className="relative">{t46}<Input id="signup-email" type="email" placeholder="you@example.com" className="pl-10 h-11" required={true} value={signupEmail} onChange={t47} disabled={t48} /></div></div>;
    $[91] = signupEmail;
    $[92] = t48;
    $[93] = t49;
  } else {
    t49 = $[93];
  }
  let t50;
  if ($[94] !== isLoading || $[95] !== signupOtp || $[96] !== signupOtpSent) {
    t50 = signupOtpSent && <div className="space-y-2"><Label htmlFor="signup-otp">Verification Code</Label><Input id="signup-otp" type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="Enter the 6-digit code" className="h-11" required={true} value={signupOtp} onChange={e_7 => setSignupOtp(e_7.target.value)} disabled={isLoading} /></div>;
    $[94] = isLoading;
    $[95] = signupOtp;
    $[96] = signupOtpSent;
    $[97] = t50;
  } else {
    t50 = $[97];
  }
  let t51;
  if ($[98] !== signupMessage) {
    t51 = signupMessage && <p className="text-sm text-muted-foreground">{signupMessage}</p>;
    $[98] = signupMessage;
    $[99] = t51;
  } else {
    t51 = $[99];
  }
  let t52;
  if ($[100] !== signupError) {
    t52 = signupError && <p className="text-sm text-destructive">{signupError}</p>;
    $[100] = signupError;
    $[101] = t52;
  } else {
    t52 = $[101];
  }
  const t53 = signupOtpSent ? isLoading ? "Verifying..." : "Verify & Create Account" : isLoading ? "Sending code..." : "Continue";
  let t54;
  if ($[102] !== isLoading || $[103] !== signupOtpSent) {
    t54 = !signupOtpSent && !isLoading && <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>;
    $[102] = isLoading;
    $[103] = signupOtpSent;
    $[104] = t54;
  } else {
    t54 = $[104];
  }
  let t55;
  if ($[105] !== isLoading || $[106] !== t53 || $[107] !== t54) {
    t55 = <Button type="submit" className="w-full h-11 rounded-md" disabled={isLoading}>{t53}{t54}</Button>;
    $[105] = isLoading;
    $[106] = t53;
    $[107] = t54;
    $[108] = t55;
  } else {
    t55 = $[108];
  }
  let t56;
  if ($[109] !== isLoading || $[110] !== signupOtpSent) {
    t56 = signupOtpSent && <Button type="button" variant="ghost" className="w-full h-11" onClick={() => {
      setSignupOtpSent(false);
      setSignupOtp("");
      setSignupMessage("");
    }} disabled={isLoading}>Use a different email</Button>;
    $[109] = isLoading;
    $[110] = signupOtpSent;
    $[111] = t56;
  } else {
    t56 = $[111];
  }
  let t57;
  if ($[112] !== t55 || $[113] !== t56) {
    t57 = <div className="flex flex-col gap-2">{t55}{t56}</div>;
    $[112] = t55;
    $[113] = t56;
    $[114] = t57;
  } else {
    t57 = $[114];
  }
  let t58;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <Link href="/terms" className="text-primary hover:underline">Terms of Use</Link>;
    $[115] = t58;
  } else {
    t58 = $[115];
  }
  let t59;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <p className="text-xs text-muted-foreground text-center mt-4">By continuing, you agree to our{" "}{t58}{" "}and{" "}<Link href="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.</p>;
    $[116] = t59;
  } else {
    t59 = $[116];
  }
  let t60;
  if ($[117] !== t39 || $[118] !== t44 || $[119] !== t49 || $[120] !== t50 || $[121] !== t51 || $[122] !== t52 || $[123] !== t57) {
    t60 = <form onSubmit={t39} className="space-y-4">{t44}{t49}{t50}{t51}{t52}{t57}{t59}</form>;
    $[117] = t39;
    $[118] = t44;
    $[119] = t49;
    $[120] = t50;
    $[121] = t51;
    $[122] = t52;
    $[123] = t57;
    $[124] = t60;
  } else {
    t60 = $[124];
  }
  let t61;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <p className="text-sm text-muted-foreground text-center mt-4">Already have an account?{" "}<button onClick={_temp} className="text-primary hover:underline">Sign in</button></p>;
    $[125] = t61;
  } else {
    t61 = $[125];
  }
  let t62;
  if ($[126] !== t36 || $[127] !== t60) {
    t62 = <TabsContent value="signup" className="space-y-6">{t35}{t36}{t38}{t60}{t61}</TabsContent>;
    $[126] = t36;
    $[127] = t60;
    $[128] = t62;
  } else {
    t62 = $[128];
  }
  let t63;
  if ($[129] !== activeTab || $[130] !== t34 || $[131] !== t62) {
    t63 = <AuthLayout><Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">{t12}{t34}{t62}</Tabs></AuthLayout>;
    $[129] = activeTab;
    $[130] = t34;
    $[131] = t62;
    $[132] = t63;
  } else {
    t63 = $[132];
  }
  return t63;
}
function _temp() {
  const loginTab = document.querySelector("[value=\"login\"]") as HTMLElement;
  loginTab?.click();
}
export default function Auth() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={null}><AuthContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}